import { useNavigate } from 'react-router-dom';
import { X, Bell, AlertTriangle, Info, CheckCircle, AlertCircle } from 'lucide-react';
import { useNotificationToasts, useMarkAsRead } from '../../hooks/useNotifications';

const priorityStyles = {
  low: 'border-gray-200 bg-gray-50',
  medium: 'border-blue-200 bg-blue-50',
  high: 'border-orange-200 bg-orange-50',
  urgent: 'border-red-200 bg-red-50',
};

const categoryIcons = {
  contract: AlertTriangle,
  budget: AlertCircle,
  work_order: Bell,
  equipment: Info,
  stock: Info,
  purchase: Info,
  preventive_maintenance: CheckCircle,
  system: Info,
  other: Bell,
};

export default function NotificationToast() {
  const navigate = useNavigate();
  const { toasts, dismissToast } = useNotificationToasts();
  const markAsRead = useMarkAsRead();

  if (toasts.length === 0) return null;

  const handleClick = (toast: typeof toasts[0]) => {
    markAsRead.mutate(toast.notification.id);
    dismissToast(toast.id);

    if (toast.notification.action_url) {
      navigate(toast.notification.action_url);
    }
  };

  return (
    <div className="fixed bottom-20 right-4 z-50 flex flex-col gap-2 md:bottom-4">
      {toasts.map((toast) => {
        const Icon = categoryIcons[toast.notification.category] || Bell;
        const priorityStyle = priorityStyles[toast.notification.priority] || priorityStyles.medium;

        return (
          <div
            key={toast.id}
            className={`animate-slide-in-right w-80 rounded-lg border-2 p-4 shadow-lg ${priorityStyle}`}
            role="alert"
          >
            <div className="flex items-start gap-3">
              <Icon className="h-5 w-5 flex-shrink-0 text-gray-600" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {toast.notification.title}
                </p>
                <p className="mt-1 text-xs text-gray-600 line-clamp-2">
                  {toast.notification.message}
                </p>
                {toast.notification.action_url && (
                  <button
                    onClick={() => handleClick(toast)}
                    className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-800"
                  >
                    {toast.notification.action_label || 'Voir details'}
                  </button>
                )}
              </div>
              <button
                onClick={() => dismissToast(toast.id)}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        );
      })}

      <style>{`
        @keyframes slide-in-right {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
